<script lang="ts">
    import { api } from '../../../stores/api';
    import { openModal } from 'svelte-modals';
    import { get } from 'svelte/store';
    import { _ } from 'svelte-i18n';

    import ModalRemoveSomething from '../Modals/ModalRemoveSomething.svelte';
    import ModalSetTargetDate from '../Modals/ModalSetTargetDate.svelte';
    import { getFilterQuery } from '../../utilities/getFilterQuery';
    import IconCalendar from '../../shared/Icons/IconCalendar.svelte';
    import ModalError from '../../shared/Modals/ModalError.svelte';
    import IconRemove from '../../shared/Icons/IconRemove.svelte';
    import IconClose from '../../shared/Icons/IconClose.svelte';
    import { jobs } from '../../../stores/store';
    import { JobApi, type ModifyResponseType } from './JobApi';
    import ModalSelectVehicle from '../Modals/ModalSelectVehicle.svelte';
    import { JobResponseSchema, type JobResponseType } from './types';
    import IconVehicle from '../../shared/Icons/IconVehicle.svelte';
    import { features } from '../../shared/Features/features';
    import { Umami } from '../../utilities/Umami';

    let selectedCount: number = 0;

    function removeSelected() {
        openModal(ModalRemoveSomething, {
            text: $_('Are you sure you want to remove selected jobs?'),
            name: `${selectedCount} ` + $_('selected'),
            onClick: () => {
                let request: Promise<{ removed: number }>;
                if ($jobs.selectMode === 'selected') {
                    request = JobApi.removeWithQuery(get(api), {
                        $and: [
                            getFilterQuery(),
                            { id: { $in: $jobs.selectedOrUnselected }, completed_as_planned: null },
                        ],
                    });
                } else {
                    request = JobApi.removeWithQuery(get(api), {
                        $and: [
                            getFilterQuery(),
                            { id: { $nin: $jobs.selectedOrUnselected }, completed_as_planned: null },
                        ],
                    });
                }
                request
                    .then(response => {
                        Umami.event('jobs_deleted', { count: selectedCount });
                        console.log('remove jobs', response);
                    })
                    .catch(error => {
                        Umami.error('jobs_deleted', error, { count: selectedCount });
                        console.warn('remove jobs', error);
                        openModal(ModalError, {
                            message: $_('some_jobs_could_not_be_modified'),
                        });
                    })
                    .finally(() => {
                        $jobs.selectedOrUnselected = [];
                        $jobs.selectMode = 'selected';
                        $jobs.forceRecount = true;
                        $jobs.forceReload = true;
                    });
            },
        });
    }

    function setTargetDateToSelected() {
        openModal(ModalSetTargetDate, {
            text: $_(`Select target date for selected jobs`) + ` (${selectedCount})`,
            onClick: () => {
                let request: Promise<ModifyResponseType>;
                if ($jobs.selectMode === 'selected') {
                    request = JobApi.modify(
                        get(api),
                        {
                            $and: [
                                getFilterQuery(),
                                { id: { $in: $jobs.selectedOrUnselected } },
                            ],
                        },
                        { target_date: $jobs.defaultTargetDate }
                    );
                } else {
                    request = JobApi.modify(
                        get(api),
                        {
                            $and: [
                                getFilterQuery(),
                                { id: { $nin: $jobs.selectedOrUnselected } },
                            ],
                        },
                        { target_date: $jobs.defaultTargetDate }
                    );
                }
                request
                    .then(response => {
                        console.log('updateTargetDate', response);
                    })
                    .catch(error => {
                        Umami.error('jobs_change_date', error, { count: selectedCount });
                        console.warn('updateTargetDate', error);
                        openModal(ModalError, {
                            message: $_('some_jobs_could_not_be_modified'),
                        });
                    })
                    .finally(() => {
                        $jobs.selectMode = 'selected';
                        $jobs.selectedOrUnselected = [];
                        $jobs.forceRecount = true;
                        $jobs.forceReload = true;
                    });
            },
        });
    }

    function setDefaultVehicleToAssign() {
        openModal(ModalSelectVehicle, {
            alreadyUsedVehicles: [],
            onClick: vehicleId => {
                let request: Promise<ModifyResponseType>;
                if ($jobs.selectMode === 'selected') {
                    request = JobApi.modify(
                        get(api),
                        {
                            $and: [
                                getFilterQuery(),
                                { id: { $in: $jobs.selectedOrUnselected } },
                                { process_stage: { $lt: 20 } },
                            ],
                        },
                        { vehicle_id: vehicleId }
                    );
                } else {
                    request = JobApi.modify(
                        get(api),
                        {
                            $and: [
                                getFilterQuery(),
                                { id: { $nin: $jobs.selectedOrUnselected } },
                                { process_stage: { $lt: 20 } },
                            ],
                        },
                        { vehicle_id: vehicleId }
                    );
                }
                request
                    .then(response => {
                        Umami.event('job_vehicle_reassigned', { vehicle_id: vehicleId, count: selectedCount });
                        console.log('updateVehicleId', response);
                    })
                    .catch(error => {
                        Umami.error('job_vehicle_reassigned', error, { count: selectedCount });
                        console.warn('updateVehicleId', error);
                        openModal(ModalError, {
                            message: $_('some_jobs_could_not_be_modified'),
                        });
                    })
                    .finally(() => {
                        $jobs.selectMode = 'selected';
                        $jobs.selectedOrUnselected = [];
                        $jobs.forceRecount = true;
                        $jobs.forceReload = true;
                    });
            },
        });
    }

    $: {
        if ($jobs.selectMode === 'selected') {
            selectedCount = $jobs.selectedOrUnselected.length;
        } else {
            selectedCount = $jobs.countFiltered.count - $jobs.selectedOrUnselected.length;
        }
    }
</script>

<div class="lm--action_bar" style="display: flex;">
    <div>
        <span class="text-span-10">{$_('Selected items')}</span>:
        <strong>{selectedCount}</strong>
    </div>
    <div class="lm--action_bar_loptions">
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div class="lm--icon_with_text" on:click={removeSelected}>
            <IconRemove scale={0.7} />
            <div>{$_('remove_jobs')}</div>
        </div>
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div class="lm--icon_with_text" on:click={setTargetDateToSelected}>
            <IconCalendar scale={0.7} />
            <div>{$_('change_target_date')}</div>
        </div>
        {#if $features?.feature_jobs_assigned_to_vehicle_by_bulk}
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <!-- svelte-ignore a11y-no-static-element-interactions -->
            <div class="lm--icon_with_text" on:click={setDefaultVehicleToAssign}>
                <IconVehicle scale={0.7} />
                <div>{$_('Select_vehicle')}</div>
            </div>
        {/if}
        <IconClose
            scale={0.7}
            on:click={() => {
                $jobs.selectedOrUnselected = [];
                $jobs.selectMode = 'selected';
            }}
        />
    </div>
</div>

<style>
    .lm--action_bar {
        font-size: 14px;
        z-index: 1000;
        height: 68px;
        color: #fff;
        background-color: #397be5;
        justify-content: space-between;
        align-items: center;
        padding: 10px 30px;
        display: none;
        position: absolute;
        top: 0;
        bottom: auto;
        left: 0;
        right: 0;
    }
    .lm--action_bar_loptions {
        grid-column-gap: 30px;
        grid-row-gap: 30px;
        /*filter: invert();*/
        color: #000;
        align-items: center;
        font-weight: 500;
        line-height: 14px;
        display: flex;
    }
    .lm--icon_with_text {
        grid-column-gap: 5px;
        grid-row-gap: 5px;
        cursor: pointer;
        align-items: center;
        display: flex;
        filter: invert();
    }
</style>
